// Default React import: parcel's automatic JSX runtime doesn't need it, but
// jest's classic runtime does — required for this component to be testable.
import React, { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTour } from '../../TourContext';
import { markOnboardingCompleted } from '../../service';
import TourExitWarning from '../TourExitWarning';

/**
 * TourGuard
 *
 * Surfaces TourExitWarning whenever the user tries to navigate away from the
 * onboarding flow (welcome carousel, checklist, or any tour). The user can:
 *   - Confirm "Leave tour"  → tour/checklist state is deactivated, navigation
 *                              to the user-requested destination proceeds, and
 *                              the sidebar's Setup Progress item is still
 *                              available to re-open the checklist
 *                              later (we deliberately do NOT persist
 *                              hasCompletedOnboarding here — this is a
 *                              temporary departure, not a permanent dismissal).
 *   - Cancel "Stay in tour" → we replace the URL back to the previous
 *                              /onboarding/* location and leave tour/checklist
 *                              state untouched.
 *
 * "Onboarding active" covers:
 *   - showTeacherOnboarding (welcome carousel)
 *   - showOnboardingComplete (checklist visible)
 *   - any of marking/draft/newTask/feedback tour active
 *
 * Render this once inside the Router subtree, near the top of the layout, so
 * it sees every navigation event during the mounted session.
 */
export default function TourGuard({ suspended = false } = {}) {
    const history = useHistory();
    const location = useLocation();
    const queryClient = useQueryClient();
    const tour = useTour() || {};
    const {
        showOnboardingComplete = false,
        markingTourActive = false,
        draftTourActive = false,
        newTaskTourActive = false,
        feedbackTourActive = false,
        wizardTourActive = false,
        setMarkingTourActive,
        setDraftTourActive,
        setNewTaskTourActive,
        setFeedbackTourActive,
        setWizardTourActive,
        setShowOnboardingComplete,
        finishCurrentTour,
        // Exit-confirmation channel (TourContext): non-null = the destination
        // awaiting the user's confirm in TourExitWarning. Shared state so the
        // sidebars can raise the warning for explicit nav-item clicks during
        // the checklist; this component stays the single owner of the dialog
        // and of the leave/stay handling.
        tourExitRequest = null,
        requestTourExit,
        clearTourExitRequest,
    } = tour;

    // `suspended` (Teacher Loop, build flag on only): App sets it while a
    // phone shows one of the logged-out public pages in the route-scoped
    // mobile shell. The guard then stands down — no redirect, no leave
    // warning, no click lock, no watchdog — WITHOUT touching the saved tour
    // state, which resumes as it was once the page is left. Always false
    // otherwise, so the guard behaves exactly as before.
    const isAnyTourActive = !suspended && Boolean(
        markingTourActive || draftTourActive || newTaskTourActive || feedbackTourActive || wizardTourActive
    );
    const isOnboardingActive = !suspended && Boolean(showOnboardingComplete || isAnyTourActive);

    const lastOnboardingPathRef = useRef(null);

    // While any tour is active, sentinel body attribute that lets globals.css
    // gate pointer-events: the rule disables clicks on `.app-container` and
    // re-enables them only on elements marked `data-tour-target="true"`. The
    // tour tooltip is portaled to document.body (outside `.app-container`) so
    // its Next/Skip/Back buttons stay interactive without needing the
    // attribute. We can't reuse the old TourClickBlocker (z-index 10001
    // transparent backdrop) because route-container at z-index 1002 creates a
    // stacking context that bounds its descendants — the blocker ended up
    // eating Open/Duplicate clicks (commit edbe81f9). CSS pointer-events is
    // immune to stacking contexts, so this approach is robust.
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        if (isAnyTourActive) {
            document.body.dataset.tourActive = 'true';
            return () => {
                delete document.body.dataset.tourActive;
            };
        }
        delete document.body.dataset.tourActive;
        return undefined;
    }, [isAnyTourActive]);

    // Stranded-tour watchdog. Every tour step's ONLY interactive surface is
    // its tooltip (Next / Back / Skip all) — everything else is click-locked
    // via body[data-tour-active]. If a step's tooltip fails to become visible
    // (its anchor never mounted after a rapid Next raced a tab/drawer
    // transition, or the component gating it unmounted), the user is stuck
    // on a dimmed screen with no way out. Invariant enforced here: a tour
    // may not stay active without a VISIBLE tooltip. After
    // STRANDED_TOUR_TIMEOUT_S consecutive seconds without one, end the tour
    // WITHOUT marking it complete (the checklist row stays unticked so the
    // user can relaunch) and reopen the checklist hub for the school tours;
    // the wizard just unlocks in place. Presence alone is not enough — a
    // tooltip whose anchor never resolved stays mounted at
    // visibility:hidden, which is exactly the stranded case.
    const STRANDED_TOUR_TIMEOUT_S = 8;
    useEffect(() => {
        if (typeof document === 'undefined' || !isAnyTourActive) return undefined;
        let misses = 0;
        const interval = setInterval(() => {
            const tip = document.querySelector('[data-tour-tooltip]');
            let visible = false;
            if (tip) {
                const cs = window.getComputedStyle(tip);
                const rect = tip.getBoundingClientRect();
                visible =
                    cs.visibility !== 'hidden' &&
                    cs.opacity !== '0' &&
                    rect.width > 0 &&
                    rect.height > 0;
            }
            if (visible) {
                misses = 0;
                return;
            }
            misses += 1;
            if (misses < STRANDED_TOUR_TIMEOUT_S) return;
            // eslint-disable-next-line no-console
            console.warn(
                `TourGuard: no visible tour tooltip for ${misses}s — recovering the stranded tour`
            );
            if (typeof finishCurrentTour === 'function') finishCurrentTour();
            if (!wizardTourActive && typeof setShowOnboardingComplete === 'function') {
                setShowOnboardingComplete(true);
            }
        }, 1000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAnyTourActive, wizardTourActive]);

    // Keyboard equivalent of the click trap. `pointer-events: none` only
    // blocks mouse/touch; a user could still Tab through sidebar nav, header
    // buttons, etc. and Space/Enter to activate them, bypassing the tour's
    // "only the tooltip + spotlight target are interactive" contract. Match
    // the tooltip via the stable `data-tour-tooltip="true"` attribute (styled-
    // components classnames are hashed in production) and any opt-in target
    // via `data-tour-target="true"`. Also exempt `[role="dialog"]` so the
    // TourExitWarning (mounted by this same TourGuard, outside .app-container)
    // keeps keyboard activation for its Stay/Leave buttons — the warning can
    // surface mid-tour via browser back/forward.
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        if (!isAnyTourActive) return undefined;
        const trap = (e) => {
            const isActivationKey = e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar';
            if (!isActivationKey) return;
            const target = e.target;
            if (!target || typeof target.closest !== 'function') return;
            const allowed = target.closest(
                '[data-tour-tooltip="true"], [data-tour-target="true"], [role="dialog"]'
            );
            if (allowed) return;
            e.preventDefault();
            e.stopPropagation();
        };
        document.addEventListener('keydown', trap, true);
        return () => document.removeEventListener('keydown', trap, true);
    }, [isAnyTourActive]);

    // Track the most recent /onboarding/* (or /tasks during checklist) so we
    // can bounce back to it when the user cancels the warning. Only record
    // routes the ACTIVE tour is allowed on — a browser-back hop onto another
    // tour's mirror route must not become the bounce target.
    useEffect(() => {
        if (location.pathname.startsWith('/onboarding/') && activeTourAllows(location.pathname)) {
            lastOnboardingPathRef.current = location.pathname + location.search + location.hash;
        } else if (showOnboardingComplete && location.pathname === '/tasks') {
            // The checklist is rendered on /tasks (production route, not a
            // mirror route) — so the "stay" target is /tasks itself, not the
            // last /onboarding/* path.
            lastOnboardingPathRef.current = '/tasks';
        }
    }, [location, showOnboardingComplete]);

    function defaultOnboardingTarget() {
        if (markingTourActive) return '/onboarding/marking';
        if (newTaskTourActive) return '/onboarding/new-task';
        if (draftTourActive) return '/onboarding/documents/demo-draft?mode=demo';
        if (wizardTourActive) return '/onboarding/documents/demo-draft?mode=demo';
        if (feedbackTourActive) return '/onboarding/submissions/demo-onboarding?mode=demo';
        if (showOnboardingComplete) return '/tasks';
        return null;
    }

    // Route family the ACTIVE tour is allowed to move within. Browser
    // back/forward can land on a DIFFERENT tour's mirror route (e.g. back
    // from /onboarding/marking into /onboarding/submissions/* visited by an
    // earlier tour) — the active tour's anchors don't exist there, so the
    // user would sit under the click-lock with no tooltip. Anything outside
    // the family gets silently bounced back to where the tour was.
    function activeTourAllows(pathname) {
        if (markingTourActive)
            return pathname === '/onboarding/marking' || pathname.startsWith('/onboarding/tools/');
        if (newTaskTourActive) return pathname === '/onboarding/new-task';
        if (draftTourActive) return pathname.startsWith('/onboarding/documents/');
        if (wizardTourActive) return pathname.startsWith('/onboarding/documents/');
        if (feedbackTourActive) return pathname.startsWith('/onboarding/submissions/');
        return true; // checklist only — any /onboarding/* push is a row click
    }

    useEffect(() => {
        if (!isOnboardingActive) return undefined;

        const unlisten = history.listen((newLocation, action) => {
            // Browser Back / Forward (POP) during onboarding must NEVER pop the
            // leave warning or strand the user. The tour itself drives the URL
            // across steps (e.g. marking steps 4-6 hop between tool routes), so
            // the back stack is full of tour-internal entries — pressing Back is
            // almost always an accidental press, not an intent to leave. Snap the
            // URL back to where the tour currently is and keep every tour flag +
            // step intact. The ONLY sanctioned exits stay: the tooltip "Skip all"
            // and the checklist close (which routes through TourExitWarning when
            // setup is incomplete). This keeps refresh + back/forward from ever
            // breaking the tour.
            if (action === 'POP') {
                const restore = lastOnboardingPathRef.current || defaultOnboardingTarget();
                if (restore) history.replace(restore);
                return;
            }
            // Allow the tour's own navigation within its route family
            // (step transitions, OnboardingComplete row-clicks). A mirror
            // route OUTSIDE the active tour's family is an accidental hop
            // (programmatic replace through older tour history): bounce
            // back silently — no warning, the tour stays exactly put.
            if (newLocation.pathname.startsWith('/onboarding/')) {
                if (activeTourAllows(newLocation.pathname)) return;
                const fallback = lastOnboardingPathRef.current || defaultOnboardingTarget() || '/tasks';
                history.replace(fallback);
                return;
            }
            // Allow /tasks when only the checklist is open (no tour active) —
            // OnboardingComplete is rendered there, so navigating to /tasks
            // IS the checklist.
            if (newLocation.pathname === '/tasks' && showOnboardingComplete && !isAnyTourActive) return;
            // Allow /tasks/new — the "Create your first task" button on
            // OnboardingComplete pushes there; it has its own onClose path.
            if (newLocation.pathname === '/tasks/new') return;
            // Allow /classes-insights — the checklist's "View class insights"
            // row sends the teacher to the real (new) Class Insights page. It's
            // a sanctioned exit (the row ticks itself, persists the dismissal
            // and closes the checklist), not an accidental escape, so it must
            // not surface the leave-tour warning (JED-1098).
            if (newLocation.pathname === '/classes-insights' && showOnboardingComplete && !isAnyTourActive) return;

            // Any other destination is an escape attempt. Save the requested
            // destination, bounce back to the onboarding location, and show
            // the warning to let the user confirm.
            const fallback = lastOnboardingPathRef.current || defaultOnboardingTarget() || '/tasks';
            const requested = newLocation.pathname + newLocation.search + newLocation.hash;
            if (typeof requestTourExit === 'function') requestTourExit(requested);
            history.replace(fallback);
        });

        // Immediate-redirect case: a tour can become active while the user
        // is already outside /onboarding/* (e.g. tour state restored from
        // localStorage on a page reload, OR a hash change). Bounce them back
        // to the onboarding location without showing the warning — they
        // didn't actively try to leave.
        const target = lastOnboardingPathRef.current || defaultOnboardingTarget();
        if (
            isAnyTourActive &&
            target &&
            (!location.pathname.startsWith('/onboarding/') ||
                !activeTourAllows(location.pathname))
        ) {
            history.replace(target);
        }

        return unlisten;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnboardingActive, isAnyTourActive, showOnboardingComplete, history]);

    function handleLeaveTour() {
        // Deactivate every tour + close the checklist so the destination
        // page renders without overlays.
        if (typeof setMarkingTourActive === 'function') setMarkingTourActive(false);
        if (typeof setDraftTourActive === 'function') setDraftTourActive(false);
        if (typeof setNewTaskTourActive === 'function') setNewTaskTourActive(false);
        if (typeof setFeedbackTourActive === 'function') setFeedbackTourActive(false);
        if (typeof setWizardTourActive === 'function') setWizardTourActive(false);
        if (typeof setShowOnboardingComplete === 'function') setShowOnboardingComplete(false);

        // Capture the SERVER-truth completion state BEFORE the cache flip below.
        const wasAlreadyCompleted = Boolean(
            queryClient.getQueryData(['profile'])?.hasCompletedOnboarding
        );
        // Suppress the in-session re-show: flip the React Query profile cache
        // so App.jsx's auto-show useEffect short-circuits on the next render
        // (it gates on profile.hasCompletedOnboarding) — otherwise the checklist
        // would re-open on top of the destination page.
        queryClient.setQueryData(['profile'], (old) => (
            old ? { ...old, hasCompletedOnboarding: true } : old
        ));
        // Persist the dismissal for a genuine first-run user opting out (so next
        // login doesn't nag them). Skip the PATCH ONLY when they were ALREADY
        // completed — i.e. a finished teacher re-watching via the Help-Centre
        // relaunch — so re-watching never re-writes their state. An unfinished
        // user (relaunch or not) still persists, and so still resumes/retires
        // onboarding consistently. (tourRelaunchRequested alone was wrong: it
        // suppressed the PATCH for unfinished relaunch users too, desyncing the
        // cache from the backend.)
        if (!wasAlreadyCompleted) {
            markOnboardingCompleted().catch((err) => {
                // eslint-disable-next-line no-console
                console.error('TourGuard: failed to persist onboarding dismissal', err);
            });
        }

        const destination = tourExitRequest;
        if (typeof clearTourExitRequest === 'function') clearTourExitRequest();
        if (destination) {
            // Defer the push so React processes the state changes above first.
            setTimeout(() => history.push(destination), 0);
        }
    }

    function handleStayInTour() {
        if (typeof clearTourExitRequest === 'function') clearTourExitRequest();
    }

    return (
        <TourExitWarning
            open={!suspended && Boolean(tourExitRequest)}
            onCancel={handleStayInTour}
            onConfirm={handleLeaveTour}
            // "Tour" copy only while a guided tour is actually running; a
            // checklist-only escape says "setup" (the user never started a tour).
            variant={isAnyTourActive ? 'tour' : 'checklist'}
        />
    );
}
